import {
  Box,
  Button,
  CheckCircleIcon,
  TextArea,
  Typography
} from "@dealer-center/core-components";
import {
  ALT_TEXT_MAX_LENGTH,
  type MLAsset
} from "@dealer-center/ddc-pages-alt-text-manager-interface";

export interface ImageCardProps {
  image: MLAsset;
  altText: string;
  savedAltText: string;
  borderLeftAccent?: string;
  isGenerating: boolean;
  isRegenerating: boolean;
  isSaving: boolean;
  hasError: boolean;
  hasGenerationError: boolean;
  hasAltText: boolean;
  nextImageId?: string;
  onAltTextChange: (imageId: string, value: string) => void;
  onSave: (image: MLAsset) => void;
  onRegenerate: (image: MLAsset) => void;
  onImageLoad: (imageId: string) => void;
  onImageError: (imageId: string) => void;
}

const isMac =
  typeof navigator !== "undefined" &&
  /macOS|Mac|iPhone|iPad/i.test(
    (navigator as Navigator & { userAgentData?: { platform: string } })
      .userAgentData?.platform ?? navigator.platform
  );

export const ImageCard = ({
  image,
  altText,
  savedAltText,
  borderLeftAccent,
  isGenerating,
  isRegenerating,
  isSaving,
  hasError,
  hasGenerationError,
  hasAltText,
  nextImageId,
  onAltTextChange,
  onSave,
  onRegenerate,
  onImageLoad,
  onImageError
}: ImageCardProps) => {
  const isSaveDisabled =
    hasError ||
    hasGenerationError ||
    !altText?.trim() ||
    altText === image.altText ||
    altText === savedAltText ||
    isSaving ||
    isGenerating ||
    isRegenerating;

  const handleTextAreaKeyDown = (
    e: React.KeyboardEvent<HTMLTextAreaElement>
  ) => {
    // Ctrl+Enter / Cmd+Enter: save in place
    if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
      e.preventDefault();
      if (!isSaveDisabled) {
        onSave(image);
      }
      return;
    }

    // Tab: save and advance to next card (only when dirty AND next card exists)
    if (e.key === "Tab" && !e.shiftKey && !isSaveDisabled) {
      onSave(image);
      if (nextImageId) {
        e.preventDefault();
        const nextTextarea = document.querySelector<HTMLTextAreaElement>(
          `[data-image-id="${nextImageId}"] textarea`
        );
        nextTextarea?.focus({ preventScroll: true });

        const nextCard = document.querySelector<HTMLElement>(
          `[data-image-card="${nextImageId}"]`
        );
        nextCard?.scrollIntoView({ behavior: "smooth", block: "nearest" });
      }
      // No nextImageId (last card): save fires, Tab navigates normally (no focus trap)
    }
  };

  return (
    <Box
      data-image-card={image.id}
      data-testid={`image-card-${image.id}`}
      key={image.id}
      sx={{
        border: "1px solid #ccc",
        borderRadius: 2,
        p: 2,
        display: "flex",
        flexDirection: "column",
        gap: 2,
        position: "relative",
        ...(borderLeftAccent && { borderLeft: borderLeftAccent }),
        "&:focus-within": {
          outline: "2px solid #1565c0",
          outlineOffset: "2px"
        }
      }}
    >
      {savedAltText && savedAltText === altText && (
        <Box
          data-testid={`saved-indicator-${image.id}`}
          sx={{
            position: "absolute",
            top: 8,
            right: 8
          }}
        >
          <CheckCircleIcon
            color="#07bc0c"
            data-testid={`saved-icon-${image.id}`}
            style={{ width: "24px", height: "24px" }}
            title="Alt text saved"
          />
        </Box>
      )}
      {hasError ? (
        <Box
          data-testid={`image-error-${image.id}`}
          sx={{
            height: "110px",
            width: "100%",
            backgroundColor: "#f5f5f5",
            border: "1px solid #ddd",
            borderRadius: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "#666"
          }}
        >
          <Typography variant="body-sm">Image not available</Typography>
        </Box>
      ) : (
        <img
          alt={image.altText || image.label || "Vehicle image"}
          data-testid={`image-${image.id}`}
          src={image.thumbName}
          style={{
            height: "110px",
            width: "100%",
            objectFit: "contain"
          }}
          onError={() => onImageError(image.id)}
          onLoad={() => onImageLoad(image.id)}
        />
      )}
      <Typography
        sx={{
          width: "140px",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap"
        }}
        variant="body-sm"
      >
        {image.label}
      </Typography>
      <div data-image-id={image.id}>
        <TextArea
          data-testid={`alt-text-textarea-${image.id}`}
          disabled={
            hasError || hasGenerationError || isGenerating || isRegenerating
          }
          maxLength={ALT_TEXT_MAX_LENGTH}
          name={`alt-text-${image.id}`}
          placeholder={
            hasError
              ? "Image unavailable"
              : hasGenerationError
                ? "Generation failed \u2014 click Regenerate"
                : isGenerating
                  ? "Generating..."
                  : "Alt text will appear here"
          }
          rows={3}
          value={altText ?? image.altText ?? ""}
          onChange={e => onAltTextChange(image.id, e.target.value)}
          onKeyDown={handleTextAreaKeyDown}
        />
      </div>
      <Typography
        data-testid={`shortcut-hint-${image.id}`}
        sx={{ color: "#888", fontSize: "11px", textAlign: "center" }}
        variant="body-sm"
      >
        Tab: save & next | {isMac ? "Cmd" : "Ctrl"}+Enter: save
      </Typography>
      <Box sx={{ display: "flex", gap: 1, justifyContent: "center" }}>
        <Button
          analyticsConfiguration={{
            eventName: "alt_text_regenerated",
            eventProperties: {
              component: "alt-text-manager",
              label: image.id,
              actionContext: "regenerate_alt_text for " + image.name,
              actionValue: altText || image.altText || ""
            }
          }}
          buttonStyle="secondary"
          data-testid={`regenerate-button-${image.id}`}
          disabled={
            hasError ||
            (!hasAltText && !hasGenerationError) ||
            isGenerating ||
            isRegenerating
          }
          isLoading={isRegenerating}
          onClick={() => onRegenerate(image)}
        >
          Regenerate
        </Button>
        <Button
          analyticsConfiguration={{
            eventName: "alt_text_accepted",
            eventProperties: {
              component: "alt-text-manager",
              label: image.id,
              actionContext: "accept_alt_text for " + image.name,
              actionValue: image.altText || altText || ""
            }
          }}
          data-testid={`accept-button-${image.id}`}
          disabled={isSaveDisabled}
          isLoading={isSaving}
          onClick={() => onSave(image)}
        >
          Save
        </Button>
      </Box>
    </Box>
  );
};
